/**
 * Contrats de lecture d'**analytics-service** (gateway `/analytics`).
 *
 * 4 endpoints, enveloppe Nawam **objet** (`{ status, message, data }` — pas la
 * forme paginée), période **obligatoire** `from`/`to` en `yyyy-MM-dd`, fenêtre
 * plafonnée à **366 jours** côté back.
 *
 * ⚠️ Cloisonnement **dérivé du JWT** : la même URL renvoie la plateforme à un
 * admin, son propre périmètre à un loueur / opérateur. Le front n'envoie
 * **aucun** identifiant d'acteur (sauf drill-down admin explicite). Voir
 * `docs/ANALYTICS_STATUS.md`.
 *
 * Disponibilité par acteur en slice 0 :
 * - **admin**    : les 4 endpoints (paiements PLATFORM-only).
 * - **owner**    : règlements + notes.
 * - **operator** : règlements uniquement.
 */

/** Enveloppe Nawam à valeur unique (non paginée). */
export interface AnalyticsEnvelope<T> {
  status?: number;
  message?: string;
  data: T;
}

/** Ligne de ventilation : `key` + effectif + montant. */
export interface BreakdownItem {
  /** Clé brute back (nom de méthode, `metadataType`, mode de paiement…). */
  key: string;
  count: number;
  value: number;
}

/** `GET /payments/summary` — **PLATFORM-only** en slice 0. */
export interface PaymentSummary {
  totalCount: number;
  successCount: number;
  /** Pourcentage 0–100 (déjà calculé back). */
  successRate: number;
  collectedAmount: number;
  feesAmount: number;
  /** Ventilation par méthode (Wave / Orange Money / Free Money). */
  byMethod: BreakdownItem[];
  /** Ventilation par `metadataType` (RENTAL / WALLET_TOPUP / LICENSE_PURCHASE…). */
  byType: BreakdownItem[];
}

/** `GET /settlements/summary` — GMV réglée + split commissions. */
export interface SettlementSummary {
  settlementCount: number;
  /** GMV : total réglé (ce que le client a payé). */
  grossAmount: number;
  /** Part plateforme prélevée. */
  platformAmount: number;
  /** Part opérateur prélevée. */
  operatorAmount: number;
  /** Ventilation cash / digital (valeur = GMV du mode). */
  byPaymentMode: BreakdownItem[];
}

/** `GET /ratings/summary`. */
export interface RatingSummary {
  ratingCount: number;
  /** ⚠️ `null` quand **aucun avis** — afficher « pas d'avis », jamais `0`. */
  average: number | null;
}

/**
 * `GET /rentals/summary` — funnel du cycle de vie + annulations + retards + GMV
 * **réservée**. Disponible aux **3 acteurs** (scope dérivé du JWT :
 * admin=plateforme, owner=son parc, operator=ses prestataires).
 *
 * ⚠️ `cancellationRate`, `avgPlannedHours` et `lateRate` valent **`null`** (pas
 * `0`) quand il n'y a **pas de base de calcul** → afficher « — », jamais « 0 % ».
 *
 * ⚠️ Post-déploiement : le funnel historique est partiel (le backfill ne
 * reconstruit pas `CONFIRMED`/`STARTED`, et le scope OPERATOR du cycle de vie ne
 * se remplit qu'au flux live) → un « CREATED → (rien) → FINISHED » sur
 * l'historique est **attendu**, pas un bug. Cf. `docs/ANALYTICS_STATUS.md` §10.
 */
export interface RentalSummary {
  createdCount: number;
  confirmedCount: number;
  startedCount: number;
  finishedCount: number;
  cancelledCount: number;
  /** % annulées / créées (funnel complet, holds expirés inclus) — `null` si 0 créée. */
  cancellationRate: number | null;
  /** GMV **réservée** (≠ encaissée). */
  bookedGmv: number;
  /** Durée moyenne planifiée des terminées (heures) — `null` si 0 terminée. */
  avgPlannedHours: number | null;
  lateCount: number;
  /** % de retours en retard — `null` si pas de base. */
  lateRate: number | null;
  lateFeeSum: number;
  /** Ventilation par transition de cycle de vie (donut, métrique = effectif). */
  byEventType: BreakdownItem[];
}

/**
 * `GET /vehicles/summary` — **deux temporalités mêlées** : le **parc**
 * (`totalVehicles`, `activeVehicles`, `underReviewVehicles`, `inactiveVehicles`,
 * `byStatus`) est à **l'instant t** (NON borné par `from`/`to`) ; seul
 * `newVehicles` (et la série) couvre la **période** `[from, to]`. **Le libeller
 * clairement dans l'UI** (piège §11).
 *
 * Disponible aux 3 acteurs (scope JWT). ⚠️ Post-déploiement : le scope OPERATOR du
 * parc démarre **au flux live** (le backfill ne résout pas l'opérateur par
 * véhicule) ; PLATFORM + OWNER sont complets dès le backfill.
 */
export interface VehicleSummary {
  /** Parc courant (non supprimés), à l'instant t. */
  totalVehicles: number;
  /** `AVAILABLE` — approuvés & non désactivés par l'owner. */
  activeVehicles: number;
  /** `PRE_UNDER_REVIEW` + `UNDER_REVIEW`. */
  underReviewVehicles: number;
  /** `UNAVAILABLE` + `UNDER_MAINTENANCE`. */
  inactiveVehicles: number;
  /** Acquis sur la **période** `[from, to]`. */
  newVehicles: number;
  /**
   * Répartition du parc par statut (donut). ⚠️ **`value` vaut toujours `0`** (le
   * parc est un **comptage**, pas un montant) → trier / afficher sur `count`.
   */
  byStatus: BreakdownItem[];
}

/** Flux d'un type de compte wallet sur la période. */
export interface WalletAccountFlow {
  /** `CREDIT` | `BALANCE` | `RECEIVABLE`. */
  accountType: string;
  movementCount: number;
  inflow: number;
  outflow: number;
  /** Flux **net** de la période — **peut être négatif** (sortie nette). */
  net: number;
}

/**
 * `GET /wallet/summary` — **flux** de trésorerie sur la période (≠ soldes, qui
 * restent le sujet #60). Scope JWT : owner/operator voient LEUR flux, admin la
 * plateforme. La **série** `wallet/series` porte le flux **net** dans `value`
 * (potentiellement **négatif**).
 */
export interface WalletSummary {
  byAccountType: WalletAccountFlow[];
}

/**
 * `GET /kyc/summary` — **admin-only** (403 sinon ; ne PAS câbler owner/operator).
 * Une entrée **par type de sujet** (CUSTOMER / OPERATOR).
 *
 * ⚠️ **Deux temporalités** : `underReview`/`approved`/`rejected`/`total` = funnel
 * à **l'instant t** ; `*InPeriod` = flux sur `[from, to]`. Le libeller.
 * `approved` est **canonicalisé** (couvre VERIFIED côté client et VALIDATED côté
 * opérateur). Cf. `docs/ANALYTICS_STATUS.md` §12.
 */
export interface KycSubjectSummary {
  /** `CUSTOMER` | `OPERATOR`. */
  subjectType: string;
  /** Backlog courant : dossiers en attente de décision (actionnable). */
  underReview: number;
  approved: number;
  rejected: number;
  total: number;
  submittedInPeriod: number;
  approvedInPeriod: number;
  rejectedInPeriod: number;
}

/**
 * `GET /users/summary` — **admin-only** (403 sinon). `totalActive` = stock à
 * **l'instant t** ; `newUsers` / `deletedUsers` = flux sur `[from, to]`. À
 * libeller distinctement. Cf. `docs/ANALYTICS_STATUS.md`.
 */
export interface UserSummary {
  totalActive: number;
  newUsers: number;
  deletedUsers: number;
}

/**
 * `GET /operators/summary` — **admin-only** (403 sinon). `newOperators` = flux
 * sur la période ; `activeOperators` / `licensedOperators` = **stocks** (état
 * courant de la dimension, sans historique des transitions → tendance fiable,
 * audit trompeur). À libeller distinctement.
 */
export interface OperatorSummary {
  newOperators: number;
  activeOperators: number;
  licensedOperators: number;
}

/** Point de série — `value` = **GMV brut** du bucket (pas la commission). */
export interface SeriesPoint {
  /** Libellé lisible du bucket (ex. `2026-W03`, `2026-01`). */
  label: string;
  /** Début du bucket (`yyyy-MM-dd`). */
  date: string;
  count: number;
  value: number;
}

export type Granularity = 'DAY' | 'WEEK' | 'MONTH';

/** Presets de période exposés dans le sélecteur (tous ≤ 366 j). */
export type PeriodPreset = '7d' | '30d' | '90d' | '365d';

/** Fenêtre résolue passée aux endpoints. */
export interface AnalyticsPeriod {
  /** `yyyy-MM-dd` inclus. */
  from: string;
  /** `yyyy-MM-dd` inclus. */
  to: string;
  /** Granularité de série adaptée à la longueur de la fenêtre. */
  granularity: Granularity;
}

const DAY_MS = 86_400_000;

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Résout un preset en fenêtre `{ from, to, granularity }`.
 *
 * La granularité suit la longueur pour garder une série lisible (~≤ 60 points) :
 * ≤ 31 j → jour, ≤ 120 j → semaine, sinon mois.
 */
export function resolvePeriod(preset: PeriodPreset): AnalyticsPeriod {
  const days = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }[preset];
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * DAY_MS);
  const granularity: Granularity = days <= 31 ? 'DAY' : days <= 120 ? 'WEEK' : 'MONTH';
  return { from: toIsoDate(from), to: toIsoDate(to), granularity };
}
